/**
 * Admin Profile Page
 * View profile information and change password
 */

import { useState, useEffect, useRef } from 'react';
import { User, Lock, Mail, Shield, Eye, EyeOff, ShieldAlert } from 'lucide-react';

import { useAuth } from '../context/AuthContext';
import { useToastNotifications } from '../hooks/useToast';
import { uploadImage, validateImageFile } from '../utils/imageUpload';
import { getUserImageSrc } from '../utils/userImage';
import Card from '../components/Card';
import Button from '../components/Button';

export default function Profile() {
  const { user, updatePassword, updateProfile, fetchAdminProfile } = useAuth();
  const { error: showError, success: showSuccess } = useToastNotifications();
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    username: '',
    image: '', // Add image field
  });

  const [passwordErrors, setPasswordErrors] = useState<Record<string, string>>({});
  const [profileErrors, setProfileErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [profileSuccessMessage, setProfileSuccessMessage] = useState('');
  const [passwordSuccessMessage, setPasswordSuccessMessage] = useState('');
  const profileFetchedRef = useRef(false);

  // Image upload states
  const [isUploading, setIsUploading] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);

  // Image upload handler
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validationError = validateImageFile(file);
    if (validationError) {
      showError(validationError);
      return;
    }

    // Show immediate preview
    const previewUrl = URL.createObjectURL(file);
    setAvatarPreview(previewUrl);

    setIsUploading(true);
    try {
      const result = await uploadImage(file);

      // Check if user is in edit mode
      if (isEditingProfile) {
        // Update profileData with new image (user is in edit mode)
        setProfileData({ ...profileData, image: result.fileName });
      } else {
        // Directly update profile with new image (no edit mode needed)
        await updateProfile({
          name: user?.name || '',
          email: user?.email || '',
          username: user?.username || '',
          image: result.fileName,
        });

        // Refresh profile data to show new image
        await fetchAdminProfile();

        // Show success toast
        showSuccess('Profile image updated successfully!', 'Your profile image has been updated.');

        // Clear preview after successful upload when not in edit mode
        setAvatarPreview(null);
      }
    } catch (error: any) {
      console.error('Image upload failed:', error);
      showError('Failed to upload image', 'Please try again later');
      // Clear preview on error
      setAvatarPreview(null);
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancelProfileEdit = () => {
    setIsEditingProfile(false);
    setProfileErrors({});
    setProfileSuccessMessage('');
    setAvatarPreview(null);
  };

  // Fetch admin profile data on component mount only once
  useEffect(() => {
    if (user && !profileFetchedRef.current) {
      profileFetchedRef.current = true;
      fetchAdminProfile().catch(console.error);
    }
  }, [user, fetchAdminProfile]);

  const validatePasswordForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!passwordData.currentPassword) {
      errors.currentPassword = 'Current password is required';
    }

    if (!passwordData.newPassword) {
      errors.newPassword = 'New password is required';
    } else if (passwordData.newPassword.length < 6) {
      errors.newPassword = 'Password must be at least 6 characters long';
    }

    if (!passwordData.confirmPassword) {
      errors.confirmPassword = 'Please confirm your new password';
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateProfileForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!profileData.name.trim()) {
      errors.name = 'Name is required';
    } else if (profileData.name.trim().length > 30) {
      errors.name = 'Name must be 30 characters or fewer';
    }

    if (!profileData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profileData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    setProfileErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleProfileEdit = () => {
    setProfileData({
      name: user?.name || '',
      email: user?.email || '',
      username: user?.username || '',
      image: user?.image || '',
    });

    // Set avatar preview for existing image
    const imageToPreview = user?.image
      ? getUserImageSrc(user.image, null)
      : null;
    setAvatarPreview(imageToPreview);

    setProfileErrors({});
    setIsEditingProfile(true);
  };

  const handleProfileUpdate = async () => {
    if (!validateProfileForm()) return;

    setIsSubmitting(true);
    setProfileSuccessMessage('');

    try {
      // Prepare update data - only include image if it was changed
      const updateData: any = {
        name: profileData.name,
        email: profileData.email,
        username: profileData.username,
      };

      // Only include image if it's different from the original user image
      if (profileData.image !== user?.image) {
        updateData.image = profileData.image;
      }

      await updateProfile(updateData);

      // Refresh profile data to show updated image
      await fetchAdminProfile();

      // Show success toast
      showSuccess('Profile updated successfully!', 'Your changes have been saved.');

      setIsEditingProfile(false);
    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to update profile';
      showError(errorMessage, 'Please check your information and try again');
      setProfileErrors({ email: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePasswordChange = async () => {
    if (!validatePasswordForm()) return;

    setIsSubmitting(true);
    setPasswordSuccessMessage('');

    try {
      await updatePassword(passwordData.currentPassword, passwordData.newPassword);
      setPasswordSuccessMessage('Password changed successfully!');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setIsChangingPassword(false);
    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to change password';
      showError(errorMessage, 'Please check your current password and try again');
      setPasswordErrors({ currentPassword: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelPasswordChange = () => {
    setIsChangingPassword(false);
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
    setPasswordErrors({});
    setPasswordSuccessMessage('');
  };

  return (
    <div className="max-w-2xl space-y-5 animate-fade-in">
      <div>
        <h1 className="text-xl font-bold text-ink">Profile</h1>
        <p className="text-sm text-ink-muted">Manage your account information and security</p>
      </div>

      {/* Profile Information */}
      <Card padding="lg">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            {/* Profile Avatar - Clickable for image upload */}
            <div className="relative">
              {isEditingProfile && (
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  id="avatar-image-upload"
                />
              )}
              {isEditingProfile ? (
                <label htmlFor="avatar-image-upload" className="cursor-pointer">
                  {isUploading ? (
                    <div className="w-16 h-16 bg-surface-muted rounded-2xl flex items-center justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                    </div>
                  ) : avatarPreview ? (
                    <>
                      <img
                        src={avatarPreview}
                        alt="Preview"
                        className="w-16 h-16 rounded-2xl object-cover"
                      />
                    </>
                  ) : user?.image ? (
                    <img
                      src={getUserImageSrc(user.image, null)!}
                      alt="Profile"
                      className="w-16 h-16 rounded-2xl object-cover hover:opacity-90 transition-opacity"
                    />
                  ) : (
                    <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center hover:bg-primary-200 transition-colors">
                      <User className="w-8 h-8 text-primary-600" />
                    </div>
                  )}
                </label>
              ) : (
                <div>
                  {user?.image ? (
                    <img
                      src={getUserImageSrc(user.image, null)!}
                      alt="Profile"
                      className="w-16 h-16 rounded-2xl object-cover"
                    />
                  ) : (
                    <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center">
                      <User className="w-8 h-8 text-primary-600" />
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="ml-4">
              <h2 className="text-xl font-semibold text-ink">{user?.name || 'Admin'}</h2>
              <p className="text-sm text-ink-muted">{user?.username || 'Administrator'}</p>
            </div>
          </div>

          {!isEditingProfile && (
            <Button variant="secondary" onClick={handleProfileEdit}>
              Edit Profile
            </Button>
          )}
        </div>

        {profileSuccessMessage && (
          <div className="mb-4 p-3.5 bg-emerald-50 border border-emerald-200 rounded-xl">
            <p className="text-sm text-emerald-700">{profileSuccessMessage}</p>
          </div>
        )}

        {isEditingProfile ? (
          <div className="space-y-3 max-w-md">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-ink-muted mb-1.5">Name</label>
              <input
                type="text"
                value={profileData.name}
                onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                maxLength={30}
                className={`input-base ${profileErrors.name ? 'border-red-400 focus:ring-red-400' : ''}`}
                placeholder="Enter your name"
              />
              <p className="mt-1 text-xs text-ink-subtle">{profileData.name.length}/30</p>
              {profileErrors.name && (
                <p className="mt-1 text-sm text-red-600">{profileErrors.name}</p>
              )}
            </div>

            {/* Username */}
            <div>
              <label className="block text-sm font-medium text-ink-muted mb-1.5">Username</label>
              <input
                type="text"
                value={profileData.username}
                onChange={(e) => setProfileData({ ...profileData, username: e.target.value })}
                className="input-base"
                placeholder="Enter username"
              />
            </div>

            {/* Profile Image */}

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-ink-muted mb-1.5">Email Address</label>
              <input
                type="email"
                value={profileData.email}
                onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                className={`input-base ${profileErrors.email ? 'border-red-400 focus:ring-red-400' : ''}`}
                placeholder="Enter your email"
              />
              {profileErrors.email && (
                <p className="mt-1 text-sm text-red-600">{profileErrors.email}</p>
              )}
            </div>

            <div className="flex gap-3 justify-end pt-2">
              <Button variant="secondary" onClick={handleCancelProfileEdit}>
                Cancel
              </Button>
              <Button onClick={handleProfileUpdate} loading={isSubmitting}>
                Update Profile
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-3 p-3 rounded-xl bg-surface-soft border border-surface-border">
              <Mail className="w-4 h-4 text-ink-subtle flex-shrink-0" />
              <div>
                <p className="text-xs text-ink-muted">Email Address</p>
                <p className="text-sm font-medium text-ink">{user?.email || 'N/A'}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 rounded-xl bg-surface-soft border border-surface-border">
              <Shield className="w-4 h-4 text-ink-subtle flex-shrink-0" />
              <div>
                <p className="text-xs text-ink-muted">Role</p>
                <p className="text-sm font-medium text-ink capitalize">{user?.role || 'admin'}</p>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Password Change */}
      <Card padding="lg">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-primary-50 flex items-center justify-center flex-shrink-0">
              <Lock className="w-4 h-4 text-primary-600" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-ink">Password</h2>
              <p className="text-xs text-ink-muted">Keep your account secure</p>
            </div>
          </div>
          {!isChangingPassword && (
            <Button variant="secondary" onClick={() => setIsChangingPassword(true)}>
              Change Password
            </Button>
          )}
        </div>

        {passwordSuccessMessage && (
          <div className="mb-4 p-3.5 bg-emerald-50 border border-emerald-200 rounded-xl">
            <p className="text-sm text-emerald-700">{passwordSuccessMessage}</p>
          </div>
        )}

        {isChangingPassword && (
          <div className="space-y-3 max-w-md">
            {/* Current Password */}
            <div>
              <label className="block text-sm font-medium text-ink-muted mb-1.5">
                Current Password
              </label>
              <div className="relative">
                <input
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={passwordData.currentPassword}
                  onChange={(e) =>
                    setPasswordData({ ...passwordData, currentPassword: e.target.value })
                  }
                  className={`input-base pr-11 ${passwordErrors.currentPassword ? 'border-red-400 focus:ring-red-400' : ''}`}
                  placeholder="Enter your current password"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showCurrentPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
              {passwordErrors.currentPassword && (
                <p className="mt-1 text-sm text-red-600">{passwordErrors.currentPassword}</p>
              )}
            </div>

            {/* New Password */}
            <div>
              <label className="block text-sm font-medium text-ink-muted mb-1.5">New Password</label>
              <div className="relative">
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  value={passwordData.newPassword}
                  onChange={(e) =>
                    setPasswordData({ ...passwordData, newPassword: e.target.value })
                  }
                  className={`input-base pr-11 ${passwordErrors.newPassword ? 'border-red-400 focus:ring-red-400' : ''}`}
                  placeholder="Enter your new password"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {passwordErrors.newPassword && (
                <p className="mt-1 text-sm text-red-600">{passwordErrors.newPassword}</p>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium text-ink-muted mb-1.5">
                Confirm New Password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={passwordData.confirmPassword}
                  onChange={(e) =>
                    setPasswordData({ ...passwordData, confirmPassword: e.target.value })
                  }
                  className={`input-base pr-11 ${passwordErrors.confirmPassword ? 'border-red-400 focus:ring-red-400' : ''}`}
                  placeholder="Confirm your new password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
              {passwordErrors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">{passwordErrors.confirmPassword}</p>
              )}
            </div>

            <div className="flex gap-3 justify-end pt-2">
              <Button variant="secondary" onClick={handleCancelPasswordChange}>
                Cancel
              </Button>
              <Button onClick={handlePasswordChange} loading={isSubmitting}>
                Update Password
              </Button>
            </div>
          </div>
        )}

        {!isChangingPassword && (
          <p className="text-sm text-ink-muted">
            You can change your password at any time for security reasons.
          </p>
        )}

        {!isChangingPassword && (
          <div className="mt-4 p-3.5 rounded-xl bg-primary-50 border border-primary-200 flex items-start gap-3">
            <ShieldAlert className="w-5 h-5 text-primary-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-primary-800 font-medium leading-relaxed">
              For security reasons, the access token expires every hour. You will be required to
              re-login after 1 hour.
            </p>
          </div>
        )}
      </Card>
    </div>
  );
}
